import { useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { DataQuery } from "@models/ISchema";
import { UPDATE_INSTRUCTOR_ASSIGNATION_TYPE } from "./graphql/updateInstructorAssignationType.mutation";
import INSTRUCTOR_TEACHINGS_QUERY from "./graphql/instructorTeachings.query.graphql";
import {
  InstructorTeachingRow,
  UpdateInstructorAssignationTypePayload,
  UpdateInstructorAssignationTypeProps,
  UpdateInstructorAssignationTypeResult,
  UseInstructorTeachingsParams,
  UseInstructorTeachingsResult,
} from "@services/types/instructor/instructorTypes";
import { getAdaptedInstructorTeachings } from "./instructorAdapters";

const INSTRUCTOR_TEACHINGS_PAGE_SIZE = 10;

export const useUpdateInstructorAssignationType = (
  props?: UpdateInstructorAssignationTypeProps,
) => {
  const [updateInstructorAssignationTypeMutation, { loading }] = useMutation(
    UPDATE_INSTRUCTOR_ASSIGNATION_TYPE,
  );

  const updateInstructorAssignationType = async ({
    scenarioId,
    originId,
    changesets,
  }: UpdateInstructorAssignationTypePayload): Promise<UpdateInstructorAssignationTypeResult> => {
    try {
      const { data } = await updateInstructorAssignationTypeMutation({
        variables: {
          scenarioId,
          originId,
          input: {
            changesets,
          },
        },
      });

      const payload = data?.cube?.updateAssignationType;

      if (payload?.commited) {
        props?.onSuccess?.();
        return { committed: true, validations: [] };
      }
      return { committed: false, validations: payload?.validations ?? [] };
    } catch (error) {
      console.error("Error updating instructor assignation type:", error);
      return { committed: false, validations: [] };
    }
  };

  return {
    isLoading: loading,
    updateInstructorAssignationType,
  };
};

export const useInstructorTeachings = ({
  instructorId,
  scenarioId,
  originId,
  shouldFetch,
}: UseInstructorTeachingsParams): UseInstructorTeachingsResult => {
  const [page, setPage] = useState(1);
  const [getInstructorTeachings, { data, loading, error }] = useLazyQuery<{
    data: DataQuery;
  }>(INSTRUCTOR_TEACHINGS_QUERY);

  useEffect(() => {
    if (!shouldFetch) {
      setPage(1);
      return;
    }

    if (instructorId) {
      getInstructorTeachings({
        variables: {
          scenarioId,
          originId,
          filter: {
            fields: { instructorId: { eq: instructorId } },
            pagination: { page, size: INSTRUCTOR_TEACHINGS_PAGE_SIZE },
          },
        },
      });
    }
  }, [shouldFetch, instructorId, page]);

  const teachings = useMemo<InstructorTeachingRow[]>(
    () => getAdaptedInstructorTeachings(data?.data?.instructorTeachings?.items ?? []),
    [data],
  );

  return {
    teachings,
    isLoading: loading,
    hasError: !!error,
    pageInfo: data?.data?.instructorTeachings?.pageInfo ?? null,
    onPageChange: setPage,
  };
};
